import { useContext, useEffect, ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '@adflow/utils';
import { IMarketingCloudUser } from '@ads/iam-library';
import { generateUserToCheck } from '../utils/user-context';

interface ProtectedRouteProps {
  children: ReactNode;
  checkPermission: (user: IMarketingCloudUser | null) => boolean | Promise<boolean>;
  redirectTo?: string;
  fallback?: ReactNode;
}

export function ProtectedRoute({
  children,
  checkPermission,
  redirectTo = '/no-access',
  fallback = null,
}: ProtectedRouteProps) {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    let isActive = true;

    const checkAccess = async () => {
      setIsChecking(true);

      const userToCheck = generateUserToCheck(user);

      if (!userToCheck) {
        if (isActive) {
          setIsChecking(false);
          navigate(redirectTo);
        }
        return;
      }

      const permission = await checkPermission(userToCheck);

      if (isActive) {
        setIsChecking(false);

        if (!permission) {
          navigate(redirectTo);
        }
      }
    };

    checkAccess();

    return () => {
      isActive = false;
    };
  }, [user, navigate, redirectTo, checkPermission]);

  if (isChecking) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
}
